import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { X } from 'lucide-react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function Modal({ open, onClose, title, children, size = 'md', className }) {
  const overlayRef = useRef(null);
  const panelRef = useRef(null);

  const trapFocus = useCallback((e) => {
    if (!panelRef.current) return;
    const focusable = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    if (e.key === 'Escape') {
      onClose?.();
    }
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    document.body.style.overflow = 'hidden';

    // Foca no primeiro elemento focável do modal
    const focusable = panelRef.current?.querySelectorAll(FOCUSABLE);
    if (focusable?.length) {
      setTimeout(() => focusable[0].focus(), 10);
    }

    document.addEventListener('keydown', trapFocus);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', trapFocus);
    };
  }, [open, trapFocus]);

  if (!open) return null;

  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-[480px]',
    lg: 'max-w-[600px]',
    xl: 'max-w-[720px]',
  };

  return createPortal(
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === overlayRef.current) onClose?.(); }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-overlay backdrop-blur-sm" aria-hidden="true" />

      {/* Panel */}
      <div
        ref={panelRef}
        className={clsx(
          'relative w-full bg-surface rounded-xl shadow-xl border border-border',
          'animate-modal-in',
          sizes[size],
          className,
        )}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h2 id="modal-title" className="text-xl font-semibold text-text-primary">
              {title}
            </h2>
            <button
              onClick={onClose}
              className="rounded-md p-1 text-text-muted hover:text-text-primary hover:bg-bg-secondary transition-colors duration-normal focus-visible:ring-2 focus-visible:ring-border-focus"
              aria-label="Fechar"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {children}
      </div>
    </div>,
    document.body,
  );
}

function ModalBody({ className, children, ...props }) {
  return (
    <div className={clsx('px-6 py-5', className)} {...props}>
      {children}
    </div>
  );
}

function ModalFooter({ className, children, ...props }) {
  return (
    <div className={clsx('flex items-center justify-end gap-3 px-6 py-4 border-t border-border', className)} {...props}>
      {children}
    </div>
  );
}

Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
